import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Popup from "../../../components/Popup";
import { getSchoolIdForUser } from "../../../utils/userContext";
import {
    downloadCandidateRegister,
    fetchMockResults,
    fetchSessionCandidates,
    processCertificatesFromMock,
    saveMockResults,
    syncManebCandidates,
    updateManebCandidate,
} from "../../../utils/manebApi";
import axios from "axios";
import { buildApiUrl } from "../../../config/api";

const ManebSessionDetail = () => {
    const { sessionId } = useParams();
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const teacherId = currentUser?._id || currentUser?.id;

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [mockResults, setMockResults] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [marksDraft, setMarksDraft] = useState({});
    const [busy, setBusy] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!sessionId) return;
        setLoading(true);
        try {
            const candData = await fetchSessionCandidates(sessionId);
            setSession(candData.session);
            setCandidates(candData.candidates || []);
            const classId = candData.session?.classId;
            if (classId) {
                const { data: subs } = await axios.get(buildApiUrl(`/SubjectsClass/${classId}`));
                setSubjects(Array.isArray(subs) ? subs : []);
            }
            try {
                const mockData = await fetchMockResults(sessionId);
                setMockResults(mockData.results || []);
            } catch {
                setMockResults([]);
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load session");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        load();
    }, [load]);

    const subjectList = useMemo(() => {
        if (!Array.isArray(subjects)) return [];
        return subjects.map((s) => ({
            id: s._id || s.id,
            name: s.subName,
            code: s.mieSyllabusCode || s.subCode || s.subName,
        }));
    }, [subjects]);

    const handleSync = async () => {
        if (!session?.classId) {
            setMessage("Session has no linked class");
            setShowPopup(true);
            return;
        }
        setBusy("sync");
        try {
            const res = await syncManebCandidates(sessionId, session.classId);
            setMessage(res.message || "Candidates synced");
            setShowPopup(true);
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Sync failed");
            setShowPopup(true);
        } finally {
            setBusy("");
        }
    };

    const handleExport = async () => {
        setBusy("export");
        try {
            await downloadCandidateRegister(sessionId);
        } catch (err) {
            setMessage(err.response?.data?.message || "Export failed");
            setShowPopup(true);
        } finally {
            setBusy("");
        }
    };

    const handleRegSave = async (candidate, value) => {
        try {
            await updateManebCandidate(candidate.id || candidate._id, { manebRegNumber: value });
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not save registration number");
            setShowPopup(true);
        }
    };

    const handleSaveMock = async () => {
        const entries = [];
        for (const cand of candidates) {
            const studentId = cand.studentId || cand.student?.id || cand.student?._id;
            for (const sub of subjectList) {
                const key = `${studentId}:${sub.id}`;
                const marks = marksDraft[key];
                if (marks === undefined || marks === "") continue;
                entries.push({
                    studentId,
                    subjectId: sub.id,
                    subjectName: sub.name,
                    paperCode: "P1",
                    marks: Number(marks),
                });
            }
        }
        if (!entries.length) {
            setMessage("Enter at least one mark");
            setShowPopup(true);
            return;
        }
        setBusy("mock");
        try {
            const res = await saveMockResults({ sessionId, schoolId, teacherId, entries });
            setMessage(res.message || "Mock results saved");
            setShowPopup(true);
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not save mock results");
            setShowPopup(true);
        } finally {
            setBusy("");
        }
    };

    const handleProcessCerts = async () => {
        setBusy("certs");
        try {
            const res = await processCertificatesFromMock(sessionId);
            setMessage(res.message || "Certificate records created");
            setShowPopup(true);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not process certificates");
            setShowPopup(true);
        } finally {
            setBusy("");
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack spacing={2}>
                <Button component={RouterLink} to="/Admin/maneb" startIcon={<ArrowBackIcon />} sx={{ alignSelf: "flex-start" }}>
                    MANEB hub
                </Button>

                <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {session?.title || "Exam session"}
                        </Typography>
                        <Chip label={session?.examType} color="warning" size="small" />
                        <Chip label={session?.sessionKind} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {session?.academicYear} · {candidates.length} candidates
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button variant="contained" startIcon={busy === "sync" ? <CircularProgress size={16} /> : <SyncIcon />} disabled={Boolean(busy)} onClick={handleSync}>
                        Sync from class roll
                    </Button>
                    <Button variant="outlined" startIcon={busy === "export" ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />} disabled={Boolean(busy)} onClick={handleExport}>
                        Export MANEB register (CSV)
                    </Button>
                </Stack>

                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Candidates" />
                    <Tab label="Mock results entry" />
                    <Tab label="Results summary" />
                </Tabs>

                {tab === 0 && (
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>MANEB reg no.</TableCell>
                                    <TableCell>Subjects</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {candidates.map((c) => (
                                    <TableRow key={c.id || c._id}>
                                        <TableCell>{c.candidateNumber}</TableCell>
                                        <TableCell>{c.student?.name}</TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                defaultValue={c.manebRegNumber}
                                                onBlur={(e) => handleRegSave(c, e.target.value)}
                                                placeholder="Registration number"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{c.subjectCodes || "—"}</Typography>
                                        </TableCell>
                                        <TableCell>{c.status}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                {tab === 1 && (
                    <Stack spacing={2}>
                        <Alert severity="info">
                            Enter mock {session?.examType} marks (0–100). Grades use the {session?.examType === "MSCE" ? "MSCE 1–9" : "A–F"} scale automatically.
                        </Alert>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Student</TableCell>
                                        {subjectList.map((s) => (
                                            <TableCell key={s.id}>{s.name}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {candidates.map((c) => {
                                        const studentId = c.studentId || c.student?.id;
                                        return (
                                            <TableRow key={c.id || c._id}>
                                                <TableCell>{c.student?.name}</TableCell>
                                                {subjectList.map((s) => {
                                                    const key = `${studentId}:${s.id}`;
                                                    return (
                                                        <TableCell key={s.id}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                inputProps={{ min: 0, max: 100 }}
                                                                value={marksDraft[key] ?? ""}
                                                                onChange={(e) =>
                                                                    setMarksDraft((d) => ({ ...d, [key]: e.target.value }))
                                                                }
                                                                sx={{ width: 72 }}
                                                            />
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                        <Button variant="contained" startIcon={<SaveOutlinedIcon />} disabled={busy === "mock"} onClick={handleSaveMock}>
                            Save mock results
                        </Button>
                    </Stack>
                )}

                {tab === 2 && (
                    <Stack spacing={2}>
                        <Button variant="outlined" disabled={busy === "certs"} onClick={handleProcessCerts}>
                            Create certificate records from mock averages
                        </Button>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Student</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Marks</TableCell>
                                        <TableCell>Grade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mockResults.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography color="text.secondary" sx={{ py: 2 }}>
                                                    No mock results recorded yet.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        mockResults.map((r) => (
                                            <TableRow key={r.id || r._id}>
                                                <TableCell>{r.student?.name}</TableCell>
                                                <TableCell>{r.subjectName}</TableCell>
                                                <TableCell>{r.marks}</TableCell>
                                                <TableCell>
                                                    {r.grade} {r.gradeLabel ? `(${r.gradeLabel})` : ""}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Stack>
                )}
            </Stack>

            <Popup showPopup={showPopup} setShowPopup={setShowPopup} title="MANEB session">
                <Typography>{message}</Typography>
            </Popup>
        </Container>
    );
};

export default ManebSessionDetail;
